import React, { useContext, useEffect } from "react";
import "./NotificationsTable.scss";
import { Typography } from "core/typography/Typography";
import { FormattedMessage, useIntl } from "react-intl";
import InputGroup from "core/inputs/InputGroup";
import Icon from "core/icon/Icon";
import InfoContext from "pages/InfoContext";
import { Link } from "react-router-dom";
import { getFormattedUtcDate } from "utils/functions";
import PropTypes from "prop-types";
import InfiniteScroll from "react-infinite-scroller";
import {
  handleArchive,
  handleBulkAction,
  handleDelete,
  handleToggleSection,
  linkWording,
  timeSincePublication,
  toggleNotificationId,
} from "utils/notificationsHelper";
import Button from "core/buttons/button/Button";

const NotificationsTable = ({
  searchValue,
  setSearchValue,
  loadMoreResults,
}) => {
  const intl = useIntl();
  const {
    isTablet,
    updateNotifications,
    deleteNotifications,
    lang,
    userScope,
    connectedUser,
    filteredResults,
    setFilteredResults,
    notificationsContent,
    notificationsTotalItems,
    isArchivedSection,
    setIsArchivedSection,
    isSearch,
    setIsSearch,
    isNotificationDetail,
    setIsNotificationDetail,
    selectedNotification,
    setSelectedNotification,
    isSelectAllNotificationsChecked,
    setIsSelectAllNotificationsChecked,
    checkedNotificationsIds,
    setCheckedNotificationsIds,
    unreadNotificationsCount,
    setNotifications,
  } = useContext(InfoContext);

  const {
    alert_id: selectedNotificationId,
    body: selectedNotificationBody,
    links: selectedNotificationLinks,
    published_at: selectedNotificationPublishedDate,
  } = selectedNotification;

  useEffect(() => {
    const filteredNotifications = notificationsContent?.filter((notification) =>
      (lang === "fr" ? notification.body.fr : notification.body.en)
        .toLowerCase()
        ?.includes(searchValue.toLowerCase())
    );
    setFilteredResults(filteredNotifications);
  }, [notificationsContent, searchValue, lang, setFilteredResults]);

  const renderReturnBtn = () => {
    return (
      <button
        className={`notifications_detail_returnBtn violet ${
          isSearch && "returnBtnPosition"
        }`}
        onClick={() =>
          isSearch ? setIsSearch(false) : setIsNotificationDetail(false)
        }
      >
        <Icon
          color="violet"
          icon="previous"
          marginRight={8}
          marginBottom={8}
          marginLeft={!isSearch && -24}
        />
        {isNotificationDetail && (
          <span>
            <FormattedMessage id="return" defaultMessage="Retour" />
          </span>
        )}
      </button>
    );
  };

  const renderSearchbar = () => {
    return (
      <>
        {isTablet && renderReturnBtn()}
        <div className="notifications_listSection_wrapper">
          <div className="notifications_listSection_wrapper_searchbar">
            <InputGroup
              icon={!isTablet && "search"}
              placeholder={intl.formatMessage({
                id: "search-button",
                defaultMessage: "Rechercher",
              })}
              onChange={(event) => setSearchValue(event.target.value)}
              value={searchValue}
            />
          </div>
        </div>
      </>
    );
  };

  const renderUsername = () => {
    return (
      connectedUser?.last_name && (
        <div className="notifications_navigation_username">
          <Typography
            className="text-2 violet"
            type="h2"
            label={`${connectedUser.first_name} ${connectedUser.last_name}`.toUpperCase()}
          />
          {isTablet && (
            <Icon
              color="light-slate-gray"
              icon="search"
              marginBottom={8}
              onClick={() => setIsSearch(true)}
            />
          )}
        </div>
      )
    );
  };

  const renderNavigation = () => {
    return (
      <div className="notifications_navigation">
        {!isTablet && renderUsername()}

        <div className="notifications_navigation_options">
          {["inbox", "archives"].map((section) => {
            const isInbox = section === "inbox";
            const isActive = isInbox ? !isArchivedSection : isArchivedSection;
            const shouldToggle = isInbox
              ? isArchivedSection
              : !isArchivedSection;
            const count =
              unreadNotificationsCount?.[isInbox ? "inbox" : "archived"];
            const handleClick = () => {
              if (shouldToggle) {
                handleToggleSection({
                  isArchivedSectionAsked: !isInbox,
                  setNotifications,
                  setCheckedNotificationsIds,
                  setSelectedNotification,
                  setIsSelectAllNotificationsChecked,
                  setIsArchivedSection,
                });
              }
            };

            return (
              <button
                key={section}
                className={`notifications_navigation_options_${section} violet btn ${
                  isActive ? "active" : ""
                }`}
                onClick={handleClick}
              >
                <span className="navlink">
                  <FormattedMessage
                    id={section}
                    defaultMessage={isInbox ? "Boîte de réception" : "Archives"}
                  />
                </span>
                <div
                  className={`notifications_navigation_options_${section}_counter`}
                >
                  <span
                    className={`notifications_navigation_options_${section}_counter_nb`}
                  >
                    {count}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderNotificationItem = ({
    notification,
    index,
    lang,
    selectedNotificationId,
    isArchivedSection,
    checkedNotificationsIds,
    isSelectAllNotificationsChecked,
    setIsSelectAllNotificationsChecked,
    setCheckedNotificationsIds,
    toggleNotificationId,
    setIsNotificationDetail,
    setSelectedNotification,
    updateNotifications,
  }) => {
    const {
      archived: isNotificationArchived,
      deleted: isNotificationDeleted,
      read: isNotificationRead,
      alert_id: notificationId,
      body: notificationBody,
      published_at: notificationPublishedDate,
    } = notification;

    const displayArchivedNotification = isArchivedSection
      ? isNotificationArchived
      : !isNotificationArchived;

    if (!displayArchivedNotification || isNotificationDeleted) return null;

    const isSelected = notificationId === selectedNotificationId;

    return (
      <div
        className={`notifications_listSection_list_listItem ${
          isNotificationRead ? "read" : "unread"
        } ${isSelected ? "selectedNotification" : ""}`}
        key={`${notificationId}-${index}`}
      >
        <div
          className={`currentIndicator ${
            isSelected ? "currentIndicatorActive" : ""
          }`}
        ></div>
        <div className="checkbox-wrapper">
          {!isNotificationRead && <div className="pastille"></div>}
          <input
            className="checkbox"
            type="checkbox"
            id={notificationId}
            checked={
              checkedNotificationsIds.includes(notificationId) ||
              isSelectAllNotificationsChecked
            }
            onChange={() => {
              setIsSelectAllNotificationsChecked(false);
              toggleNotificationId(notificationId, setCheckedNotificationsIds);
            }}
          />
        </div>
        <div className="notifications_listSection_list_listItem_text-wrapper">
          <button
            type="text"
            className="text violet"
            onClick={() => {
              setIsNotificationDetail(true);
              setSelectedNotification(notification);

              if (!isNotificationRead) {
                updateNotifications({
                  data: {
                    alerts_ids: [notificationId],
                    read: true,
                    all: false,
                  },
                });
                notification.read = true;
              }
            }}
          >
            {notificationBody[lang]}
          </button>
          <span className="notifications_listSection_list_listItem_since light-slate-gray">
            {timeSincePublication(notificationPublishedDate, lang)}
          </span>
        </div>
      </div>
    );
  };

  const renderNotificationsList = () => {
    return (
      <div className="notifications_listSection">
        {!isTablet && renderSearchbar()}

        <div className="notifications_listSection_options">
          <div className="notifications_listSection_options_selectall text">
            <div className="checkbox-wrapper">
              <input
                type="checkbox"
                id="select-all"
                checked={isSelectAllNotificationsChecked}
                onChange={() => {
                  setCheckedNotificationsIds([]);
                  setIsSelectAllNotificationsChecked(
                    !isSelectAllNotificationsChecked
                  );
                }}
              />
            </div>
            <label>
              <FormattedMessage
                id="select-all"
                defaultMessage="Tout séléctionner"
              />
            </label>
          </div>

          <div className="notifications_listSection_options_actions">
            <Button
              className="notifications_listSection_options_actions_trash-2"
              color="white"
              onClick={() =>
                handleBulkAction("delete", {
                  isSelectAllNotificationsChecked,
                  checkedNotificationsIds,
                  isArchivedSection,
                  notificationsContent,
                  selectedNotification,
                  setSelectedNotification,
                  setIsNotificationDetail,
                  setIsSelectAllNotificationsChecked,
                  setNotifications,
                  deleteNotifications,
                })
              }
            >
              <Icon
                icon="trash-2"
                color="light-slate-gray"
                marginRight={10}
                marginBottom={5}
                marginLeft={-10}
              />
            </Button>
            {!isArchivedSection && (
              <Button
                className="notifications_listSection_options_actions_archive"
                color="white"
                onClick={() =>
                  handleBulkAction("archive", {
                    isSelectAllNotificationsChecked,
                    checkedNotificationsIds,
                    notificationsContent,
                    selectedNotification,
                    setSelectedNotification,
                    setIsNotificationDetail,
                    setIsSelectAllNotificationsChecked,
                    setNotifications,
                    updateNotifications,
                  })
                }
              >
                <Icon
                  icon="archive"
                  color="light-slate-gray"
                  marginRight={10}
                  marginBottom={5}
                  marginLeft={-10}
                />
              </Button>
            )}
          </div>
        </div>

        <div className="notifications_listSection_list">
          {notificationsTotalItems > 0 && (
            <InfiniteScroll
              pageStart={1}
              loadMore={loadMoreResults}
              hasMore={notificationsContent?.length < notificationsTotalItems}
              useWindow={false}
              getScrollParent={() =>
                document.querySelector(".notifications_listSection_list")
              }
            >
              {(searchValue ? filteredResults : notificationsContent)?.map(
                (notification, index) =>
                  renderNotificationItem({
                    notification,
                    index,
                    lang,
                    selectedNotificationId,
                    isArchivedSection,
                    checkedNotificationsIds,
                    isSelectAllNotificationsChecked,
                    setIsSelectAllNotificationsChecked,
                    setCheckedNotificationsIds,
                    toggleNotificationId,
                    setIsNotificationDetail,
                    setSelectedNotification,
                    updateNotifications,
                  })
              )}
            </InfiniteScroll>
          )}
        </div>
      </div>
    );
  };

  const renderNotificationDetail = () => {
    return (
      <div className="notifications_detail">
        {selectedNotificationId && (
          <>
            {isTablet && renderReturnBtn()}
            <div className="notifications_detail_actions">
              <Button
                className="notifications_detail_actions_delete"
                color="white"
                onClick={() =>
                  handleDelete(
                    selectedNotificationId,
                    deleteNotifications,
                    isArchivedSection,
                    notificationsContent,
                    setSelectedNotification,
                    setIsNotificationDetail
                  )
                }
              >
                <Icon
                  icon="trash-2"
                  color="light-slate-gray"
                  marginRight={10}
                  marginBottom={5}
                  marginLeft={isTablet ? 0 : -10}
                />
                <span>
                  <FormattedMessage
                    id={"delete"}
                    defaultMessage={"Supprimer"}
                  />
                </span>
              </Button>

              {!isArchivedSection && (
                <Button
                  className="notifications_detail_actions_archive"
                  color="white"
                  onClick={() =>
                    handleArchive(
                      selectedNotificationId,
                      notificationsContent,
                      updateNotifications,
                      setSelectedNotification,
                      setIsNotificationDetail
                    )
                  }
                >
                  <Icon
                    icon="archive"
                    color="light-slate-gray"
                    marginRight={10}
                    marginBottom={5}
                    marginLeft={-10}
                  />
                  <span>
                    <FormattedMessage
                      id={"archive"}
                      defaultMessage={"Archiver"}
                    />
                  </span>
                </Button>
              )}
            </div>

            <p className="notifications_detail_date">
              {getFormattedUtcDate(selectedNotificationPublishedDate, lang)}
            </p>

            <p type="text" className="notifications_detail_text">
              {selectedNotificationBody[lang]}
            </p>

            {selectedNotificationLinks?.length > 0 &&
              selectedNotificationLinks.map((link) => (
                <Link
                  className="notifications_detail_link btn btn-standard btn-outline deep-purple"
                  to={`/${userScope}/${link?.entity}/${link?.slug}`}
                  target="_blank"
                  key={link.index}
                >
                  {linkWording({ entity: link.entity, text: link.text })}
                </Link>
              ))}
          </>
        )}
      </div>
    );
  };

  const ifSearchDisplay = isSearch ? renderSearchbar() : renderUsername();

  return (
    <div className="notifications">
      {isTablet && !isNotificationDetail && ifSearchDisplay}

      {isTablet
        ? !isNotificationDetail && renderNavigation()
        : renderNavigation()}

      {!isTablet && renderNotificationsList()}

      {isTablet && !isNotificationDetail
        ? renderNotificationsList()
        : renderNotificationDetail()}
    </div>
  );
};

export default NotificationsTable;

NotificationsTable.propTypes = {
  searchValue: PropTypes.string,
  setSearchValue: PropTypes.func,
};
